import { useCallback, useEffect, useState } from 'react'
import { listFiles } from '../utils/fileBrowserApi'

/**
 * Drives the S3 file browser: current bucket + prefix, folders/files at that
 * level, and ContinuationToken pagination ("Load more").
 */
export default function useS3FileBrowser(initialBucket = 'private') {
  const [bucket, setBucket] = useState(initialBucket)
  const [prefix, setPrefix] = useState('')
  const [folders, setFolders] = useState([])
  const [files, setFiles] = useState([])
  const [nextToken, setNextToken] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState(null)

  const fetchLevel = useCallback(async (targetBucket, targetPrefix) => {
    setLoading(true)
    setError(null)
    try {
      const data = await listFiles({ bucket: targetBucket, prefix: targetPrefix })
      setFolders(data.folders || [])
      setFiles(data.files || [])
      setNextToken(data.nextContinuationToken || null)
    } catch (err) {
      console.error('Error listing files:', err)
      setError(err.message)
      setFolders([])
      setFiles([])
      setNextToken(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLevel(bucket, prefix)
  }, [bucket, prefix, fetchLevel])

  const navigateTo = newPrefix => setPrefix(newPrefix)

  const switchBucket = newBucket => {
    if (newBucket === bucket) return
    setBucket(newBucket)
    setPrefix('')
  }

  const refresh = () => fetchLevel(bucket, prefix)

  const loadMore = async () => {
    if (!nextToken || loadingMore) return
    setLoadingMore(true)
    try {
      const data = await listFiles({ bucket, prefix, continuationToken: nextToken })
      setFolders(prev => [...prev, ...(data.folders || [])])
      setFiles(prev => [...prev, ...(data.files || [])])
      setNextToken(data.nextContinuationToken || null)
    } catch (err) {
      console.error('Error loading more files:', err)
      setError(err.message)
    } finally {
      setLoadingMore(false)
    }
  }

  return {
    bucket,
    prefix,
    folders,
    files,
    loading,
    loadingMore,
    error,
    hasMore: Boolean(nextToken),
    navigateTo,
    switchBucket,
    refresh,
    loadMore,
  }
}
